import { i } from 'react-router/dist/development/fog-of-war-CGNKxM4z';
import apiClient from '../base/apiClient';

export const flowApi = {
  getProcessFlows: async () => {
    const response = await apiClient.get('/Administration/Flow/ListAll');
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const flows = response.data.flows || [];
    return {
      data: flows.map((flow: any) => ({
        id: flow.flowId,
        name: flow.name,
        description: flow.description,
        isActive: flow.isActive,
        iconUrl: flow.iconUrl ? `${apiClient.defaults.baseURL}${flow.iconUrl}` : null,
        location: flow.locations,
        isSpecial: flow.isSpecial || false,
      })),
    };
  },

  uploadIcon: async (params: { flowId: number; file: File }) => {
    const { flowId, file } = params;
    const formData = new FormData();
    formData.append('icon', file);
    const response = await apiClient.post(`/Administration/Flow/UploadIcon?flowId=${flowId}`, formData);
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  uploadStateIcon: async (params: { flowStateId: number; file: File }) => {
    const { flowStateId, file } = params;
    const formData = new FormData();
    formData.append('icon', file);
    const response = await apiClient.post(`/Administration/FlowState/UploadIcon?flowStateId=${flowStateId}`, formData);
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  getListFlowLocation: async (params: { flowId: number }) => {
    const { flowId } = params;
    const response = await apiClient.get(`/Administration/Flow/LocationList?flowId=${flowId}`);
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const locations = response.data.locations || [];
    return { data: locations.map((loc: any) => ({ orderIndex: loc.orderIndex, name: loc.name })), total: locations.length };
  },

  addFlowLocation: async (params: { flowId: number; locationName: string }) => {
    const { flowId, locationName } = params;
    const response = await apiClient.get(`/Administration/Flow/LocationAdd?flowId=${flowId}&locationName=${locationName}`);
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  removeFlowLocation: async (params: { flowId: number; locationIndex: number }) => {
    const { flowId, locationIndex } = params;
    const response = await apiClient.get(`/Administration/Flow/LocationRemove?flowId=${flowId}&locationIndex=${locationIndex}`);
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  getFlowStatesByFlowId: async (params: { flowId: number }) => {
    const { flowId } = params;
    const response = await apiClient.get(`/Administration/FlowState/ListByFlowId?flowId=${flowId}`);
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const flowStates = response.data.flowStates || [];
    return {
      data: flowStates.map((state: any) => ({
        id: state.flowStateId,
        status: state.status,
        flowId: state.flowId,
        canClientCancel: state.canClientCancel,
        canClientAdvance: state.canClientAdvance,
        canContractorCancel: state.canContractorCancel,
        canContractorAdvance: state.canContractorAdvance,
        showTracker: state.showTracker,
        locationIndex: state.locationIndex,
        nextState: state.nextState,
        state: state.state,
        iconUrl: state.iconUrl ? `${apiClient.defaults.baseURL}${state.iconUrl}` : null,
      })),
    };
  },

  createFlow: async (params: { name: string; description: string; isActive: boolean }) => {
    const { name, description, isActive = true } = params;
    const response = await apiClient.post('/Administration/Flow/Create', { name, description, isActive });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return {
      data: {
        id: response.data.flowId || 0,
        name,
        description,
        isActive: response.data.isActive || false,
        iconUrl: response.data.iconUrl || null,
      },
    };
  },

  deleteFlow: async (params: { flowId: number }) => {
    const { flowId } = params;
    const response = await apiClient.delete('/Administration/Flow/Delete', { params: { flowId } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { flowId } };
  },

  updateFlow: async (params: { id: number; data: any }) => {
    const { id, data } = params;
    const response = await apiClient.put('/Administration/Flow/Update', {
      flowId: id,
      isActive: data.isActive,
      name: data.name,
      description: data.description,
      iconUrl: data.iconUrl || '',
      isSpecial: data.isSpecial || false,
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return {
      data: {
        id: response.data.flowId || id,
        isActive: response.data.isActive,
        name: response.data.name,
        description: response.data.description,
        iconUrl: response.data.iconUrl || null,
        isSpecial: response.data.isSpecial || false,
      },
    };
  },

  createFlowState: async (resource: string, params: any) => {
    const { data } = params;
    if (resource === 'FlowState/Create') {
      const response = await apiClient.post(
        `/Administration/FlowState/Create`,
        data,
        { withCredentials: true }
      );

      if (response.data.errorMessage) {
        throw new Error(response.data.errorMessage);
      }
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      return {
        data: {
          id: response.data.flowStateId || 0,
          ...data,
        },
      };
    }
  },

  deleteFlowState: async (params: { flowId: number; flowStateId: number }) => {
    const { flowId, flowStateId } = params;
    const response = await apiClient.delete('/Administration/FlowState/Delete', { params: { flowId, flowStateId } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { flowId, flowStateId } };
  },

  updateFlowState: async (params: { data: any }) => {
    const { data } = params;
    const response = await apiClient.put('/Administration/FlowState/Update', data);
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data };
  },

  changeLocationOrder: async (params: { flowId: number; locationIndex: number; newOrderIndex: number }) => {
    const { flowId, locationIndex, newOrderIndex } = params;
    const response = await apiClient.get('/Administration/Flow/LocationChangeOrder', {
      params: { flowId, locationIndex, newOrderIndex },
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },
};